'use client';
import React from 'react';
import LazyLoad from 'react-lazyload';
import styled from '@benzinga/themetron';
import { SessionContext } from '@benzinga/session-context';
import { AuthenticationManager, User } from '@benzinga/session';
import { useIsUserLoggedIn } from '@benzinga/user-context';
import { ContentManager } from '@benzinga/content-manager';

export interface AskTheExpertProps {
  attrs: {
    data: {
      expert_name: string;
      expert_bio: string;
      expert_image: string;
      heading: string;
    };
  };
}

export const AskTheExpert: React.FC<AskTheExpertProps> = ({ attrs }) => {
  const [question, setQuestion] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [showEmailField, setShowEmailField] = React.useState(true);
  const [userData, setUserData] = React.useState<User | null>(null);
  const [userEmail, setUserEmail] = React.useState('');

  const isLoggedIn = useIsUserLoggedIn();
  const session = React.useContext(SessionContext);
  React.useEffect(() => {
    const getPropsClientSide = async () => {
      const userReq = await session.getManager(AuthenticationManager).getAuthSession(true);
      if (userReq?.ok) {
        setUserData(userReq?.ok.user);
        setUserEmail(userReq?.ok.user.email);
      }
    };

    if (isLoggedIn) {
      setShowEmailField(false);
      getPropsClientSide();
    }
  }, [isLoggedIn, session]);

  const submitForm = () => {
    if (!question && !userEmail) {
      setError('Please Fill The Question & Email.');
      return;
    }
    setIsLoading(true);
    session
      .getManager(ContentManager)
      .saveAskTheExpertComment({
        benzinga_uid: userData?.id ?? null,
        comment: question,
        email: userData?.email ?? userEmail ?? null,
        ref_url: window.location.href,
        uuid: userData?.uuid ?? null,
      })
      .then(res => {
        if (res.ok) {
          const { id, message, status } = res.ok;
          if (status === 'success') {
            setMessage(message);
          } else {
            setError('Something went wrong');
            console.log('Something went wrong');
          }
        } else {
          setError('Something went wrong');
          console.log('Something went wrong');
        }
      })
      .catch(err => {
        console.log('Something went wrong');
        console.log(err);
      });
  };

  return (
    <LazyLoad>
      <AskTheExpertContainer className="ask-the-expert border border-t-4 shadow-sm p-4 mb-4">
        <h3 className="mb-3">{attrs.data.heading}</h3>
        <div className="featured-articles-items flex flex-wrap items-center mb-3 justify-center md:justify-start">
          {attrs.data.expert_name && (
            <div className="flex mb-5">
              <div className="w-1/3  expert-image image-container text-center">
                <img
                  alt="Benzinga Expert"
                  className="rounded-full border-2 border-solid border-white w-20 h-20"
                  src={
                    attrs.data.expert_image ||
                    'https://s3.amazonaws.com/wp-uploads.benzinga-money.prod/wp-content/uploads/2022/02/08045009/logo-192-e1644313833132.png'
                  }
                  title="Benzinga Expert"
                />
              </div>
              <div className="details-container text-left mb:pl-0">
                <h3 className="mb-0">{attrs.data.expert_name || ''}</h3>
                <p className="mb-0">{attrs.data.expert_bio || ''}</p>
              </div>
            </div>
          )}

          <div className="question-container">
            {error && <div className="error-msg mt-4">{error}</div>}
            {message && <div className="response-msg mt-4">{message}</div>}

            {showEmailField ? (
              <div className="input-container flex flex-col mb-4">
                <label className="share-question-label text-blue-light_40 font-manrope text-blue-nocollight_30 text-sm not-italic w-full leading-5 m-0 font-bold uppercase ">
                  Email
                </label>
                <input
                  className="border p-2 w-2/3 rounded"
                  name="email"
                  onChange={e => {
                    setUserEmail(e.target.value);
                  }}
                  placeholder="Enter Your Email"
                  type="email"
                />
              </div>
            ) : (
              <>
                <input name="uuid" type="hidden" value={userData?.uuid} />
                <input name="email" type="hidden" value={userData?.email} />
                <input name="id" type="hidden" value={userData?.id} />
              </>
            )}
            <label className="share-question-label text-blue-light_40 font-manrope text-blue-nocollight_30 text-sm not-italic w-full leading-5 m-0 font-bold uppercase mb-3 block">
              Share your Question
            </label>
            <textarea
              className="border p-2 w-2/3 rounded shadow-none w-full"
              cols={50}
              onChange={e => {
                setQuestion(e.target.value);
              }}
              placeholder="Enter Your Question"
              rows={4}
            ></textarea>
            <input
              className="submit-btn button-v5 rounded cursor-pointer"
              disabled={isLoading}
              id="submit-expert-form"
              onClick={submitForm}
              type="button"
              value="Submit your question"
            />
          </div>
        </div>
      </AskTheExpertContainer>
    </LazyLoad>
  );
};

const AskTheExpertContainer = styled.div`
  border-top-color: ${({ theme }) => theme.colorPalette.orange500};
  box-shadow:
    rgb(0 0 0 / 0%) 0px 0px 0px 0px,
    rgb(0 0 0 / 0%) 0px 0px 0px 0px,
    rgb(163 163 163 / 24%) 0px 0px 6px 1px;
  h2.heading {
    font-family: sans-serif;
    font-weight: 700;
    margin: 0;
    padding: 0;
    margin-top: 1rem;
    margin-bottom: 1rem;
    color: #395173;
    font-size: 30px;
  }
  .bg-blue-light_100 {
    background-color: #1a79ff0d;
  }
  .text-blue-nocollight_30 {
    color: #283d59;
  }
  .question-ans-wrapper {
    border: solid 1px #c3ddfd;
    border-radius: 4px;
  }
  .error-msg,
  .response-msg {
    border-radius: 0.25rem;
    border-width: 2px;
    padding: 0.5rem 1.5rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 700;
    text-transform: capitalize;
  }
  .error-msg {
    border-color: #ff0000;
    color: #ff0000;
  }
  .response-msg {
    border-color: #0aa046;
    color: #0aa046;
  }
  .expert-image {
    min-width: 80px;
  }

  .button-v5 {
    margin-bottom: 0;
    display: inline-block;
    height: 2.5rem;
    white-space: nowrap;
    border-width: 0;
    background-color: #002d45;
    color: #fff !important;
    padding: 0 0.875rem;
    text-align: center;
    font-family: sans-serif;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    text-decoration: none;
    font-size: 1rem;
    line-height: 2.5rem;
    text-shadow: 1px 0 0 rgb(0 0 0 / 10%);
    appearance: none;
    border-radius: 0;
    &:hover {
      box-shadow:
        rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0) 0px 0px 0px 0px;
    }
  }
`;
